import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Checkbox,
  Menu,
  TablePagination
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  LockReset as LockResetIcon,
  LockOpen as LockOpenIcon,
  VerifiedUser as VerifiedIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  PhonelinkLock as MfaIcon,
  Send as SendIcon,
  PersonOff as PersonOffIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Workspaces as WorkspacesIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toast } from '../../utils/toastUtils';
import api from '../../services/api/client';

function StatCard({ title, value, icon: Icon, trend, trendValue, color = 'primary', subtitle }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                lineHeight: 1.3
              }}
            >
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ my: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 0.5,
                  wordBreak: 'break-word',
                  lineHeight: 1.3
                }}
              >
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {trend >= 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography
                  variant="body2"
                  color={trend >= 0 ? 'success.main' : 'error.main'}
                >
                  {trend >= 0 ? '+' : ''}{trend}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  vs mois dernier
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: (theme) => theme.palette[color]?.light ? `${color}.light` : 'action.hover',
              color: `${color}.main`,
              opacity: 0.9
            }}
          >
            <Icon fontSize="large" />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Map URL paths to tab indices
const TAB_PATHS = {
  '/admin': 0,
  '/admin/companies': 0,
  '/admin/users': 1,
  '/admin/workspaces': 2,
  '/admin/audit-logs': 3,
  '/admin/logs': 3,  // Alias for audit-logs
  '/admin/suspicious': 4
};

const TAB_TO_PATH = {
  0: '/admin/companies',
  1: '/admin/users',
  2: '/admin/workspaces',
  3: '/admin/audit-logs',
  4: '/admin/suspicious'
};

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Determine initial tab from URL path
  const getInitialTab = () => {
    const path = location.pathname;
    return TAB_PATHS[path] ?? 0;
  };

  const [tabValue, setTabValue] = useState(getInitialTab);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [workspaceSearchTerm, setWorkspaceSearchTerm] = useState('');
  const [logsSearchTerm, setLogsSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [newUserData, setNewUserData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'analyst',
    company_id: '',
    send_invitation: true
  });
  const [companiesPage, setCompaniesPage] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [workspacesPage, setWorkspacesPage] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const rowsPerPage = 10;

  // Sync tab with URL when location changes (e.g., browser back/forward)
  useEffect(() => {
    const newTab = TAB_PATHS[location.pathname] ?? 0;
    if (newTab !== tabValue) {
      setTabValue(newTab);
    }
  }, [location.pathname]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    let hasError = false;

    // Load each data source independently to prevent one failure from blocking others
    const loadStats = async () => {
      try {
        const res = await api.get('/admin/stats');
        setStats(res.data);
      } catch (error) {
        // 401 errors are handled by the API interceptor (redirect to login)
        if (error.response?.status !== 401) {
          console.error('Error loading stats:', error);
          hasError = true;
        }
      }
    };

    const loadCompanies = async () => {
      try {
        const res = await api.get('/admin/companies?limit=100');
        setCompanies(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error('Error loading companies:', error);
          hasError = true;
        }
      }
    };

    const loadUsers = async () => {
      try {
        const res = await api.get('/admin/users?limit=100');
        setUsers(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error('Error loading users:', error);
          hasError = true;
        }
      }
    };

    const loadWorkspaces = async () => {
      try {
        const res = await api.get('/admin/workspaces?limit=100');
        setWorkspaces(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error('Error loading workspaces:', error);
          hasError = true;
        }
      }
    };

    const loadAuditLogs = async () => {
      try {
        const res = await api.get('/admin/audit-logs?limit=50');
        setAuditLogs(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error('Error loading audit logs:', error);
          hasError = true;
        }
      }
    };

    // Execute all requests in parallel for better performance
    await Promise.all([
      loadStats(),
      loadCompanies(),
      loadUsers(),
      loadWorkspaces(),
      loadAuditLogs()
    ]);

    if (hasError) {
      toast.error('Certaines données n\'ont pas pu être chargées');
    }

    setLoading(false);
  };

  const handleViewCompany = async (companyId) => {
    try {
      const res = await api.get(`/admin/companies/${companyId}`);
      setSelectedCompany(res.data);
      setShowCompanyDialog(true);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setSelectedUser(res.data);
      setShowUserDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleUpdateCompanyStatus = async (companyId, newStatus) => {
    try {
      await api.patch(`/admin/companies/${companyId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // User Actions
  const handleUserAction = async (action, userId, data = {}) => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'resend-verification':
          await api.post(`/admin/users/${userId}/resend-verification`);
          toast.success('E-mail de vérification envoyé');
          break;
        case 'send-password-reset':
          await api.post(`/admin/users/${userId}/send-password-reset`);
          toast.success('E-mail de réinitialisation envoyé');
          break;
        case 'reset-password':
          await api.post(`/admin/users/${userId}/reset-password`, { new_password: data.password });
          toast.success('Mot de passe modifié');
          setShowResetPasswordDialog(false);
          setNewPassword('');
          break;
        case 'unlock':
          await api.post(`/admin/users/${userId}/unlock`);
          toast.success('Compte déverrouillé');
          break;
        case 'verify-email':
          await api.post(`/admin/users/${userId}/verify-email`);
          toast.success('E-mail vérifié');
          break;
        case 'activate':
          await api.post(`/admin/users/${userId}/activate`);
          toast.success('Utilisateur activé');
          break;
        case 'deactivate':
          await api.post(`/admin/users/${userId}/deactivate`);
          toast.success('Utilisateur désactivé');
          break;
        case 'disable-mfa':
          await api.post(`/admin/users/${userId}/disable-mfa`);
          toast.success('MFA désactivé');
          break;
        case 'resend-invitation':
          await api.post(`/admin/users/${userId}/resend-invitation`);
          toast.success('Invitation renvoyée');
          break;
        case 'delete':
          if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur\u202F? Cette action est irréversible.')) {
            await api.delete(`/admin/users/${userId}`);
            toast.success('Utilisateur supprimé');
          }
          break;
        default:
          break;
      }
      loadData();
      if (selectedUser?.id === userId) {
        handleViewUser(userId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    } finally {
      setActionLoading(false);
      setUserMenuAnchor(null);
    }
  };

  const handleCreateUser = async () => {
    setActionLoading(true);
    try {
      await api.post('/admin/users/create', newUserData);
      toast.success('Utilisateur créé avec succès');
      setShowCreateUserDialog(false);
      setNewUserData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'analyst',
        company_id: '',
        send_invitation: true
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await api.get('/admin/users/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format workspace/company name for consistent display
  // Converts ALL CAPS names to Title Case while preserving mixed case names
  const formatName = (name) => {
    if (!name) return '-';
    // Only convert if the name is ALL CAPS (common from business registries)
    if (name === name.toUpperCase() && name.length > 3) {
      return name
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return name;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'admin': return 'warning';
      case 'manager': return 'info';
      case 'analyst': return 'primary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super administrateur';
      case 'admin': return 'Administrateur';
      case 'manager': return 'Gestionnaire';
      case 'analyst': return 'Analyste';
      case 'viewer': return 'Lecteur';
      default: return role;
    }
  };

  const getCompanyStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'suspended': return 'Suspendu';
      case 'pending': return 'En attente';
      case 'inactive': return 'Inactif';
      default: return status;
    }
  };

  const getWorkspaceStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'completed': return 'Terminé';
      case 'archived': return 'Archivé';
      case 'pending': return 'En attente';
      case 'inactive': return 'Inactif';
      default: return status;
    }
  };

  // Get color for audit log resource type
  const getResourceTypeColor = (resourceType) => {
    if (!resourceType) return 'default';
    const type = resourceType.toLowerCase();
    // AI/LLM related
    if (type.includes('ai') || type.includes('ollama') || type.includes('llm') || type.includes('model')) {
      return 'secondary';
    }
    // Authentication/Security
    if (type.includes('auth') || type.includes('login') || type.includes('token') || type.includes('mfa')) {
      return 'warning';
    }
    // Users/Admin
    if (type.includes('user') || type.includes('admin')) {
      return 'info';
    }
    // Scans/Security testing
    if (type.includes('scan') || type.includes('vuln') || type.includes('pentest')) {
      return 'error';
    }
    // Workspaces/Companies
    if (type.includes('workspace') || type.includes('company') || type.includes('tenant')) {
      return 'primary';
    }
    return 'default';
  };

  // Get French label for common resource types
  const getResourceTypeLabel = (resourceType) => {
    if (!resourceType) return '-';
    const type = resourceType.toLowerCase();
    const labels = {
      'ai': 'Modèles IA',
      'ollama': 'Modèles IA',
      'llm': 'Modèles IA',
      'auth': 'Authentification',
      'login': 'Connexion',
      'users': 'Utilisateurs',
      'admin': 'Administration',
      'scans': 'Scans',
      'workspaces': 'Espaces de travail',
      'companies': 'Entreprises',
      'system': 'Système',
      'settings': 'Paramètres',
      'reports': 'Rapports',
      'vulnerabilities': 'Vulnérabilités'
    };
    // Check for exact match first
    if (labels[type]) return labels[type];
    // Check for partial match
    for (const [key, label] of Object.entries(labels)) {
      if (type.includes(key)) return label;
    }
    // Capitalize first letter for unknown types
    return resourceType.charAt(0).toUpperCase() + resourceType.slice(1);
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Administration
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadData}>
          Actualiser
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Entreprises"
            value={stats?.total_companies || 0}
            icon={BusinessIcon}
            trend={stats?.growth?.companies}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Utilisateurs"
            value={stats?.total_users || 0}
            icon={PeopleIcon}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total scans"
            value={stats?.total_scans || 0}
            icon={SecurityIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenus (mois)"
            value={formatCurrency(stats?.revenue_current_month || 0)}
            subtitle="Abonnements et crédits"
            icon={MoneyIcon}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => { setTabValue(v); navigate(TAB_TO_PATH[v] || '/admin'); }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Entreprises" icon={<BusinessIcon />} iconPosition="start" sx={{ minWidth: 'fit-content', textTransform: 'none', whiteSpace: 'nowrap' }} />
          <Tab label="Utilisateurs" icon={<PeopleIcon />} iconPosition="start" sx={{ minWidth: 'fit-content', textTransform: 'none', whiteSpace: 'nowrap' }} />
          <Tab label="Espaces de travail" icon={<WorkspacesIcon />} iconPosition="start" sx={{ minWidth: 'fit-content', textTransform: 'none', whiteSpace: 'nowrap' }} />
          <Tab label="Logs d'audit" icon={<SecurityIcon />} iconPosition="start" sx={{ minWidth: 'fit-content', textTransform: 'none', whiteSpace: 'nowrap' }} />
          <Tab label="Activité suspecte" icon={<WarningIcon />} iconPosition="start" sx={{ minWidth: 'fit-content', textTransform: 'none', whiteSpace: 'nowrap' }} />
        </Tabs>
      </Paper>

      {/* Companies Tab */}
      {tabValue === 0 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
            <TextField
              placeholder="Rechercher…"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 300 }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entreprise</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Utilisateurs</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Scans</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Créé le</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies
                  .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .slice(companiesPage * rowsPerPage, companiesPage * rowsPerPage + rowsPerPage)
                  .map((company) => (
                    <TableRow key={company.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">{company.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {company.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={company.plan}
                          size="small"
                          color={company.plan === 'free' ? 'default' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            company.status === 'active' ? 'Entreprise active avec tous les accès' :
                            company.status === 'suspended' ? 'Entreprise suspendue, accès bloqués' :
                            company.status === 'pending' ? 'En attente d\'approbation ou de paiement' :
                            company.status === 'inactive' ? 'Entreprise inactive, compte fermé' :
                            'Statut inconnu'
                          }
                        >
                          <Chip
                            label={getCompanyStatusLabel(company.status)}
                            size="small"
                            color={
                              company.status === 'active' ? 'success' :
                              company.status === 'suspended' ? 'error' : 'warning'
                            }
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">{company.users_count}</TableCell>
                      <TableCell align="center">{company.scans_count}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(company.created_at)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleViewCompany(company.id)}>
                          <ViewIcon />
                        </IconButton>
                        {company.status === 'active' ? (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleUpdateCompanyStatus(company.id, 'suspended')}
                          >
                            <BlockIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleUpdateCompanyStatus(company.id, 'active')}
                          >
                            <CheckIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={companies.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).length}
            page={companiesPage}
            onPageChange={(e, p) => setCompaniesPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            labelRowsPerPage="Lignes par page"
            getItemAriaLabel={(type) => type === 'previous' ? 'Page précédente' : 'Page suivante'}
            sx={{ '& .MuiTablePagination-displayedRows': { fontSize: '0.875rem' } }}
          />
        </Paper>
      )}

      {/* Users Tab */}
      {tabValue === 1 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Rechercher…"
              size="small"
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 300 }}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportUsers}
            >
              Exporter en CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowCreateUserDialog(true)}
            >
              Nouvel utilisateur
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>E-mail vérifié</TableCell>
                  <TableCell>MFA</TableCell>
                  <TableCell>Dernière connexion</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers
                  .slice(usersPage * rowsPerPage, usersPage * rowsPerPage + rowsPerPage)
                  .map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ maxWidth: 280, minWidth: 200 }}>
                      <Tooltip title={user.email} placement="top-start">
                        <Typography fontWeight="medium" sx={{ wordBreak: 'break-word' }}>{user.email}</Typography>
                      </Tooltip>
                      <Typography variant="body2" color="text.secondary">
                        {user.first_name} {user.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(user.role)}
                        size="small"
                        color={getRoleColor(user.role)}
                      />
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Chip label="Actif" size="small" color="success" />
                      ) : (
                        <Chip label="Inactif" size="small" color="error" />
                      )}
                      {user.locked_until && (
                        <Chip label="Verrouillé" size="small" color="warning" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {user.email_verified ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {user.mfa_enabled ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <CloseIcon color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {user.last_login_at ? (
                        formatDateTime(user.last_login_at)
                      ) : (
                        <Typography variant="body2" color="text.secondary">Jamais</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Voir détails">
                        <IconButton size="small" onClick={() => handleViewUser(user.id)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setUserMenuAnchor(e.currentTarget);
                            setMenuUser(user);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={usersPage}
            onPageChange={(e, p) => setUsersPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            labelRowsPerPage="Lignes par page"
            getItemAriaLabel={(type) => type === 'previous' ? 'Page précédente' : 'Page suivante'}
            sx={{ '& .MuiTablePagination-displayedRows': { fontSize: '0.875rem' } }}
          />
        </Paper>
      )}

      {/* Workspaces Tab */}
      {tabValue === 2 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Rechercher…"
              size="small"
              value={workspaceSearchTerm}
              onChange={(e) => setWorkspaceSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 300 }}
            />
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Légende :
              </Typography>
              <Chip label="Actif" size="small" color="success" sx={{ height: 22 }} />
              <Chip label="Terminé" size="small" color="info" sx={{ height: 22 }} />
              <Chip label="En attente" size="small" color="warning" sx={{ height: 22 }} />
              <Chip label="Inactif" size="small" color="error" sx={{ height: 22 }} />
              <Chip label="Archivé" size="small" color="default" sx={{ height: 22 }} />
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Espace de travail</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Propriétaire</TableCell>
                  <TableCell>Entreprise</TableCell>
                  <TableCell align="center">Scans</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Créé le</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workspaces
                  .filter(w =>
                    w.name?.toLowerCase().includes(workspaceSearchTerm.toLowerCase()) ||
                    w.client_name?.toLowerCase().includes(workspaceSearchTerm.toLowerCase())
                  )
                  .slice(workspacesPage * rowsPerPage, workspacesPage * rowsPerPage + rowsPerPage)
                  .map((workspace) => (
                    <TableRow key={workspace.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">{formatName(workspace.name)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatName(workspace.client_name)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            workspace.status === 'active' ? 'Espace de travail en cours d\'utilisation' :
                            workspace.status === 'completed' ? 'Toutes les tâches sont terminées' :
                            workspace.status === 'archived' ? 'Espace archivé, accès en lecture seule' :
                            workspace.status === 'pending' ? 'En attente de configuration ou validation' :
                            workspace.status === 'inactive' ? 'Espace de travail inactif' :
                            'Statut inconnu'
                          }
                        >
                          <Chip
                            label={getWorkspaceStatusLabel(workspace.status)}
                            size="small"
                            color={
                              workspace.status === 'active' ? 'success' :
                              workspace.status === 'completed' ? 'info' :
                              workspace.status === 'archived' ? 'default' :
                              workspace.status === 'inactive' ? 'error' : 'warning'
                            }
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, minWidth: 120 }}>
                        <Tooltip title={workspace.owner_email || '-'} placement="top-start">
                          <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {workspace.owner_email || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {workspace.company_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{workspace.scans_count}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(workspace.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={workspaces.filter(w =>
              w.name?.toLowerCase().includes(workspaceSearchTerm.toLowerCase()) ||
              w.client_name?.toLowerCase().includes(workspaceSearchTerm.toLowerCase())
            ).length}
            page={workspacesPage}
            onPageChange={(e, p) => setWorkspacesPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            labelRowsPerPage="Lignes par page"
            getItemAriaLabel={(type) => type === 'previous' ? 'Page précédente' : 'Page suivante'}
            sx={{ '& .MuiTablePagination-displayedRows': { fontSize: '0.875rem' } }}
          />
        </Paper>
      )}

      {/* Audit Logs Tab */}
      {tabValue === 3 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
            <TextField
              placeholder="Rechercher…"
              size="small"
              value={logsSearchTerm}
              onChange={(e) => setLogsSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 300 }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Horodatage</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell align="center">Type de ressource</TableCell>
                  <TableCell>Adresse IP</TableCell>
                  <TableCell align="center">Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Aucun log d'audit disponible
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs
                    .filter(log =>
                      log.action?.toLowerCase().includes(logsSearchTerm.toLowerCase()) ||
                      log.resource_type?.toLowerCase().includes(logsSearchTerm.toLowerCase()) ||
                      log.ip_address?.toLowerCase().includes(logsSearchTerm.toLowerCase())
                    )
                    .slice(logsPage * rowsPerPage, logsPage * rowsPerPage + rowsPerPage)
                    .map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          {formatDateTime(log.timestamp)}
                        </TableCell>
                        <TableCell>{log.action}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getResourceTypeLabel(log.resource_type)}
                            size="small"
                            color={getResourceTypeColor(log.resource_type)}
                          />
                        </TableCell>
                        <TableCell>{log.ip_address || '-'}</TableCell>
                        <TableCell align="center">
                          {log.success ? (
                            <Chip label="Succès" size="small" color="success" />
                          ) : (
                            <Chip label="Échec" size="small" color="error" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={auditLogs.filter(log =>
              log.action?.toLowerCase().includes(logsSearchTerm.toLowerCase()) ||
              log.resource_type?.toLowerCase().includes(logsSearchTerm.toLowerCase()) ||
              log.ip_address?.toLowerCase().includes(logsSearchTerm.toLowerCase())
            ).length}
            page={logsPage}
            onPageChange={(e, p) => setLogsPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            labelRowsPerPage="Lignes par page"
            getItemAriaLabel={(type) => type === 'previous' ? 'Page précédente' : 'Page suivante'}
            sx={{ '& .MuiTablePagination-displayedRows': { fontSize: '0.875rem' } }}
          />
        </Paper>
      )}

      {/* Suspicious Activity Tab */}
      {tabValue === 4 && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Cette section affiche les tentatives de connexion échouées et les activités suspectes.
          </Alert>
          <Button onClick={async () => {
            const res = await api.get('/admin/audit-logs/suspicious');
            console.log(res.data);
            toast.info('Voir la console');
          }}>
            Charger les alertes
          </Button>
        </Paper>
      )}

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        slotProps={{
          paper: {
            sx: { minWidth: 280 }
          }
        }}
      >
        <MenuItem onClick={() => { handleUserAction('resend-verification', menuUser?.id); }}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Renvoyer l'e-mail de vérification</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleUserAction('send-password-reset', menuUser?.id); }}>
          <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Envoyer lien de réinitialisation</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setSelectedUser(menuUser); setShowResetPasswordDialog(true); setUserMenuAnchor(null); }}>
          <ListItemIcon><LockResetIcon fontSize="small" color="warning" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Changer mot de passe</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleUserAction('resend-invitation', menuUser?.id); }}>
          <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Renvoyer invitation</ListItemText>
        </MenuItem>
        <Divider />
        {menuUser?.locked_until && (
          <MenuItem onClick={() => { handleUserAction('unlock', menuUser?.id); }}>
            <ListItemIcon><LockOpenIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Déverrouiller le compte</ListItemText>
          </MenuItem>
        )}
        {!menuUser?.email_verified && (
          <MenuItem onClick={() => { handleUserAction('verify-email', menuUser?.id); }}>
            <ListItemIcon><VerifiedIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Forcer la vérification e-mail</ListItemText>
          </MenuItem>
        )}
        {!menuUser?.is_active ? (
          <MenuItem onClick={() => { handleUserAction('activate', menuUser?.id); }}>
            <ListItemIcon><PersonIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Activer le compte</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { handleUserAction('deactivate', menuUser?.id); }}>
            <ListItemIcon><PersonOffIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Désactiver le compte</ListItemText>
          </MenuItem>
        )}
        {menuUser?.mfa_enabled && (
          <MenuItem onClick={() => { handleUserAction('disable-mfa', menuUser?.id); }}>
            <ListItemIcon><MfaIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Désactiver MFA</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { handleUserAction('delete', menuUser?.id); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}>Supprimer</ListItemText>
        </MenuItem>
      </Menu>

      {/* Company Detail Dialog */}
      <Dialog open={showCompanyDialog} onClose={() => setShowCompanyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails de l'entreprise
        </DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Nom</Typography>
                <Typography variant="h6">{selectedCompany.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">SIREN</Typography>
                <Typography>{selectedCompany.siren || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">E-mail</Typography>
                <Typography>{selectedCompany.contact_email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Statut</Typography>
                <Chip
                  label={getCompanyStatusLabel(selectedCompany.status)}
                  color={selectedCompany.status === 'active' ? 'success' : 'error'}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Abonnement</Typography>
                {selectedCompany.subscription ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography>Plan: {selectedCompany.subscription.plan_name}</Typography>
                      <Typography>Statut: {selectedCompany.subscription.status}</Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info">Pas d'abonnement</Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Utilisateurs ({selectedCompany.users?.length || 0})</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>E-mail</TableCell>
                        <TableCell>Rôle</TableCell>
                        <TableCell>Actif</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedCompany.users?.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell sx={{ maxWidth: 250, minWidth: 150 }}>
                            <Tooltip title={user.email} placement="top-start">
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{user.email}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip label={getRoleLabel(user.role)} size="small" color={getRoleColor(user.role)} />
                          </TableCell>
                          <TableCell>
                            {user.is_active ? <CheckIcon color="success" /> : <BlockIcon color="error" />}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => { setShowCompanyDialog(false); handleViewUser(user.id); }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompanyDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={showUserDialog} onClose={() => setShowUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Détails de l'utilisateur
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">E-mail</Typography>
                <Typography variant="h6">{selectedUser.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Nom complet</Typography>
                <Typography>{selectedUser.first_name} {selectedUser.last_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Rôle</Typography>
                <Chip label={getRoleLabel(selectedUser.role)} color={getRoleColor(selectedUser.role)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Entreprise</Typography>
                <Typography>{selectedUser.company_name || '-'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Statut du compte</Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Actif</Typography>
                {selectedUser.is_active ? (
                  <Chip label="Oui" color="success" size="small" />
                ) : (
                  <Chip label="Non" color="error" size="small" />
                )}
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">E-mail vérifié</Typography>
                {selectedUser.email_verified ? (
                  <Chip label="Oui" color="success" size="small" />
                ) : (
                  <Chip label="Non" color="warning" size="small" />
                )}
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">MFA</Typography>
                {selectedUser.mfa_enabled ? (
                  <Chip label="Actif" color="success" size="small" />
                ) : (
                  <Chip label="Inactif" color="default" size="small" />
                )}
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Approbation admin</Typography>
                {selectedUser.pending_admin_approval ? (
                  <Chip label="En attente" color="warning" size="small" />
                ) : (
                  <Chip label="Approuvé" color="success" size="small" />
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Sécurité</Typography>
              </Grid>

              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Tentatives échouées</Typography>
                <Typography color={selectedUser.failed_login_attempts > 0 ? 'error.main' : 'text.primary'}>
                  {selectedUser.failed_login_attempts || 0}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Verrouillé jusqu'à</Typography>
                <Typography>
                  {formatDateTime(selectedUser.locked_until)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Bloqué</Typography>
                {selectedUser.blocked_at ? (
                  <Chip label={selectedUser.blocked_reason || 'Bloqué'} color="error" size="small" />
                ) : (
                  <Typography>-</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Connexion</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Dernière connexion</Typography>
                <Typography>
                  {selectedUser.last_login_at ? formatDateTime(selectedUser.last_login_at) : 'Jamais'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Dernière IP</Typography>
                <Typography>{selectedUser.last_login_ip || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Date de création</Typography>
                <Typography>{formatDateTime(selectedUser.created_at)}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            startIcon={<EmailIcon />}
            onClick={() => handleUserAction('resend-verification', selectedUser?.id)}
            disabled={selectedUser?.email_verified}
          >
            Renvoyer la vérification
          </Button>
          <Button
            startIcon={<LockResetIcon />}
            onClick={() => handleUserAction('send-password-reset', selectedUser?.id)}
          >
            Réinitialiser le mot de passe
          </Button>
          {selectedUser?.locked_until && (
            <Button
              startIcon={<LockOpenIcon />}
              color="success"
              onClick={() => handleUserAction('unlock', selectedUser?.id)}
            >
              Déverrouiller
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setShowUserDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onClose={() => setShowResetPasswordDialog(false)}>
        <DialogTitle>Changer le mot de passe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Définir un nouveau mot de passe pour {selectedUser?.email}
          </Typography>
          <TextField
            label="Nouveau mot de passe"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Minimum 8 caractères"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowResetPasswordDialog(false); setNewPassword(''); }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => handleUserAction('reset-password', selectedUser?.id, { password: newPassword })}
            disabled={newPassword.length < 8 || actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onClose={() => setShowCreateUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Créer un nouvel utilisateur</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="E-mail"
                type="email"
                fullWidth
                required
                placeholder="email@exemple.fr"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Prénom"
                fullWidth
                value={newUserData.first_name}
                onChange={(e) => setNewUserData({ ...newUserData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Nom"
                fullWidth
                value={newUserData.last_name}
                onChange={(e) => setNewUserData({ ...newUserData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Rôle</InputLabel>
                <Select
                  value={newUserData.role}
                  label="Rôle"
                  onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                >
                  <MenuItem value="viewer">Lecteur</MenuItem>
                  <MenuItem value="analyst">Analyste</MenuItem>
                  <MenuItem value="manager">Gestionnaire</MenuItem>
                  <MenuItem value="admin">Administrateur</MenuItem>
                  <MenuItem value="super_admin">Super administrateur</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Entreprise</InputLabel>
                <Select
                  value={newUserData.company_id}
                  label="Entreprise"
                  onChange={(e) => setNewUserData({ ...newUserData, company_id: e.target.value })}
                >
                  <MenuItem value="">Aucune</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUserData.send_invitation}
                    onChange={(e) => setNewUserData({ ...newUserData, send_invitation: e.target.checked })}
                  />
                }
                label="Envoyer l'e-mail d'invitation"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateUserDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!newUserData.email || actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
